import { Dispatch, SetStateAction, useRef, useState } from "react"

// ** Third Party Components
import axios from "axios"
import { parseCookies } from "nookies"
import { useMutation } from "@tanstack/react-query"
import { FileText, Plus, Trash, Upload } from "lucide-react"

// ** Custom Element
import { Label } from "@components/common/label"
import { Button } from "@components/common/button"
import { Progress } from "@components/common/progress"
import { Separator } from "@components/common/separator"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTrigger,
} from "@components/common/draggable-dialog"

// ** Types
import { DictType, ProjectFile } from "@pma/types"
import { ACCESS_TOKEN, BLOCKED_EXTENSIONS } from "@pma/utils/constants"

// ** Custom Hooks
import { toast } from "@hooks/use-toast"
import useTranslator from "@hooks/use-translator"

// ** Variables
import {
  ADD_FILES_TO_PROJECT_ENDPOINT,
  DIGITAL_OCEAN_FILES_ENDPOINT,
  SERVER_URL,
} from "@pma/utils/api-routes"

// ** helpers
import { cn } from "@pma/utils/helpers"

interface Props {
  projectId: string
  revalidate?: CallableFunction
  folder: DictType
  mainView?: boolean
}

export default function UploadFile({ projectId, revalidate, folder, mainView }: Props) {
  const { translate, locale } = useTranslator()

  const isHebrew = locale === "he"
  const fileUploadCount = useRef(0)
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [file, setFile] = useState<any[]>([])
  const [progress, setProgress] = useState(0)
  const [isOpen, setIsOpen] = useState(false)
  const [uploadError, setUploadError] = useState(false)
  const [nameUpdates, setNameUpdates] = useState<string[]>([])

  const handleFileChange = (event: any) => {
    setProgress(0)
    setUploadError(false)
    if (event.target.files) {
      const fileArr = event.target.files

      let validFileType = true
      let validFileSize = true
      const maxSize = 1000 * 1024 * 1024 // 1000MB in bytes

      let totalSize = file.reduce((acc: number, f: any) => acc + f.size, 0)
      for (let index = 0; index < fileArr.length; index++) {
        const nameArr = fileArr[index].name.split(".")
        const fileExt = nameArr[nameArr.length - 1]
        if (BLOCKED_EXTENSIONS.includes(fileExt)) {
          validFileType = false
          break
        }
        totalSize += fileArr[index].size
      }

      if (totalSize > maxSize) {
        validFileSize = false
      }

      if (!validFileType) {
        toast({
          title: translate("Error"),
          description: translate("Please select a valid file!"),
          variant: "error",
        })
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = ""
        }
      } else if (!validFileSize) {
        toast({
          title: translate("Error"),
          description: translate("File size must be under 1000MB!"),
          variant: "error",
        })
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = ""
        }
      } else {
        setFile((prev) => [...prev, ...event.target.files])
        setNameUpdates((prev) => [...prev, ...[...event.target.files].map((file) => file?.name)])
      }
    }
  }

  const handleDrop = (event: any) => {
    event.preventDefault()
    if (event.dataTransfer.files) {
      const fileArr = event.dataTransfer.files

      let validFileType = true
      let validFileSize = true
      const maxSize = 1000 * 1024 * 1024 // 1000MB in bytes

      let totalSize = file.reduce((acc: number, f: any) => acc + f.size, 0)
      for (let index = 0; index < fileArr.length; index++) {
        const nameArr = fileArr[index].name.split(".")
        const fileExt = nameArr[nameArr.length - 1]
        if (BLOCKED_EXTENSIONS.includes(fileExt)) {
          validFileType = false
          break
        }
        totalSize += fileArr[index].size
      }

      if (totalSize > maxSize) {
        validFileSize = false
      }

      if (!validFileType) {
        toast({
          title: translate("Error"),
          description: translate("Please select a valid file!"),
          variant: "error",
        })
      } else if (!validFileSize) {
        toast({
          title: translate("Error"),
          description: translate("File size must be under 1000MB!"),
          variant: "error",
        })
      } else {
        setFile((prev) => [...prev, ...event.dataTransfer.files])
        setNameUpdates((prev) => [
          ...prev,
          ...[...event.dataTransfer.files].map((file) => file?.name),
        ])
      }
    }
  }

  const handleCancel = () => {
    setFile([])
    setProgress(0)
    setNameUpdates([])
  }

  const handleAddMoreFiles = () => {
    if (fileInputRef.current) fileInputRef.current.click()
  }

  const uploadToProject = async (files: ProjectFile[]) => {
    const file_ids = files?.map((f) => {
      return f.id
    })
    const body = folder?.isRoot ? { project_id: projectId } : { folder_id: folder?.id }
    const response = await axios.post(
      SERVER_URL + ADD_FILES_TO_PROJECT_ENDPOINT,
      {
        ...body,
        files_id: file_ids,
        inform_users: true,
      },
      { headers: { Authorization: `Bearer ${accessToken}` } },
    )

    return response.data
  }
  const uploadToProjectMutation = useMutation({
    mutationKey: ["add-files-to-project", projectId],
    mutationFn: uploadToProject,
    onSuccess: (data) => {
      toast({
        title: translate("Success"),
        description: `${fileUploadCount.current} ${fileUploadCount.current === 1 ? translate("File uploaded successfully") : translate("Files uploaded successfully")}`,
        variant: "success",
      })

      // Reset input
      handleCancel()
      fileUploadCount.current = 0
      revalidate && revalidate()
      setIsOpen(false)
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message || error?.response?.data?.errorMessage
      setUploadError(true)
      toast({
        title: translate("Error"),
        description: errorMessage || translate("Unable to add file at this time"),
        variant: "error",
      })
    },
  })

  const uploadFile = async () => {
    if (!file || file.length === 0) {
      return
    }

    const formData = new FormData()

    const renamedFiles = file.map((val: File, index: number) => {
      const updatedName = nameUpdates[index] || val.name
      const fileNameArr = updatedName.split(".")
      const initialFileName = val.name.split(".")
      const fileExt = initialFileName[initialFileName.length - 1]

      return new File([val], `${fileNameArr[0]}.${fileExt}`, { type: val.type })
    })

    renamedFiles.forEach((renamedFile) => {
      formData.append("files", renamedFile)
    })

    const url = `${DIGITAL_OCEAN_FILES_ENDPOINT}?token=${accessToken}`

    const response = await axios.post(url, formData, {
      headers: {
        accept: "*/*",
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent: any) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
        setProgress(percentCompleted)
      },
    })

    return response?.data
  }

  const uploadFileMutation = useMutation({
    mutationKey: ["upload-file"],
    mutationFn: uploadFile,
    onSuccess: (data) => {
      const payload = data as ProjectFile[]
      fileUploadCount.current = payload.length

      // Link digital ocean file to  project
      uploadToProjectMutation.mutate(payload)
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message || error?.response?.data?.errorMessage
      setUploadError(true)
      toast({
        title: translate("Error"),
        description: errorMessage || "Unable to upload file at this time",
        variant: "error",
      })
    },
  })

  const handleUploadClick = () => {
    const hasEmptyName = nameUpdates.some((name) => name.trim() === "")
    if (hasEmptyName) {
      toast({
        title: translate("Error"),
        description: translate("Please enter a valid file name!"),
        variant: "error",
      })
      return
    }

    uploadFileMutation.mutate()
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open)
        if (!open) handleCancel()
      }}
    >
      <DialogTrigger>
        <Tooltip>
          <TooltipTrigger>
            {mainView ? (
              <div className='flex items-center gap-2 rounded-md bg-slate-200/50 p-1 shadow'>
                <Upload className='text-foreground' size={16} />
                <small className='text-xs'>{translate("Upload file")}</small>{" "}
              </div>
            ) : (
              <Button variant='ghost' size={"icon"} className='bg-slate-200/50'>
                <Upload className='text-foreground' size={16} />
              </Button>
            )}
          </TooltipTrigger>
          <TooltipContent className='bg-foreground'>
            {translate("Add file to folder")}
          </TooltipContent>
        </Tooltip>
      </DialogTrigger>

      <DialogContent
        className='w-full max-w-xl lg:max-w-2xl xl:max-w-3xl 2xl:max-w-5xl'
        dragHandleClass='.nested-draggable'
      >
        <div className='flex w-full flex-col items-center justify-between'>
          <h4 className={cn("pb-2 font-semibold")}>
            {translate("File Upload")}{" "}
            <small>{file?.length > 0 && `(${file?.length} ${translate("files selected")})`}</small>
          </h4>
          <Separator className='w-full' />
        </div>
        <div className={`nested-draggable flex items-center justify-between`}>
          <input
            id='file-input'
            type='file'
            className='sr-only text-black'
            onChange={handleFileChange}
            multiple
            ref={fileInputRef}
          />
        </div>

        <div
          onDragOver={(event) => event.preventDefault()}
          onDrop={handleDrop}
          className={cn(
            "my-0 flex flex-1 items-center justify-center rounded-md py-0 text-center",
            file.length > 0
              ? "overflow-y-auto border-none"
              : "border border-dashed border-primary/50",
          )}
        >
          <div className='grid h-[300px] w-full grid-cols-1 gap-2 p-0.5 sm:grid-cols-2 md:grid-cols-3'>
            {file?.length === 0 && (
              <p className='col-span-full flex w-full flex-col items-center justify-center text-muted-foreground'>
                {translate("Drag and drop a file")}
                <Button variant={"outline"} className='mt-2 flex justify-center'>
                  <Label htmlFor='file-input' className={cn("", isHebrew ? "rtl" : "rtl")}>
                    {translate("Choose Files")}
                  </Label>
                </Button>
              </p>
            )}
            {file &&
              file?.map((file, index) => (
                <RenderUploadPreview
                  file={file}
                  index={index}
                  setNameUpdates={setNameUpdates}
                  setFile={setFile}
                  nameUpdates={nameUpdates}
                  key={`${file.name}-${index}`}
                />
              ))}
            {file?.length > 0 && (
              <div className='col-span-1 flex h-full w-full justify-center'>
                <Button
                  variant='outline'
                  className='flex h-28 w-full items-center gap-2 border-dashed border-primary/50'
                  onClick={handleAddMoreFiles}
                >
                  <Plus className='h-6 w-6 text-primary' />
                  <span>{translate("Add More Files")}</span>
                </Button>
              </div>
            )}
          </div>{" "}
        </div>
        {progress !== 0 && (
          <div className='space-y-2'>
            <div className='flex items-center justify-between'>
              <p className='text-sm text-muted-foreground'>
                {uploadError ? translate("Error") : translate("Uploading")}...
              </p>
              <p className='text-sm text-muted-foreground'>{progress}%</p>
            </div>
            <Progress value={progress} variant={uploadError ? "destructive" : "default"} />
          </div>
        )}
        <DialogFooter className='flex flex-row justify-evenly'>
          <DialogClose className='flex'>
            <Button variant='outline' onClick={handleCancel}>
              {translate("Cancel")}{" "}
            </Button>
          </DialogClose>
          <Button
            variant='default'
            disabled={
              uploadFileMutation?.isPending ||
              file.length === 0 ||
              uploadToProjectMutation?.isPending
            }
            loading={uploadFileMutation?.isPending || uploadToProjectMutation?.isPending}
            onClick={handleUploadClick}
          >
            {translate("Upload")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface PreviewProps {
  file: File
  setFile: Dispatch<SetStateAction<any[]>>
  setNameUpdates: Dispatch<SetStateAction<string[]>>
  nameUpdates: string[]
  index: number
}
export const RenderUploadPreview = ({
  file,
  nameUpdates,
  setNameUpdates,
  index,
  setFile,
}: PreviewProps) => {
  const { translate } = useTranslator()

  const isImage = file?.type?.startsWith("image/")

  const removeAttachment = (index: number) => {
    setFile((prev) => prev.filter((_, i) => i !== index))
    setNameUpdates((prev) => prev.filter((_, i) => i !== index))
  }

  const renameFile = (newName: string) => {
    setNameUpdates((prev) => prev.map((name, i) => (i === index ? newName : name)))
  }

  return (
    <div key={`${file.name}-${index}`} className='relative col-span-1 h-44 w-full'>
      <div className='group relative h-28 w-full overflow-hidden rounded-md border bg-muted/30'>
        <button
          onClick={() => removeAttachment(index)}
          className='absolute right-1 top-1 z-10 rounded-full bg-white/80 p-1 transition-colors hover:bg-red-500 hover:text-white'
          aria-label='Remove file'
        >
          <Trash className='h-4 w-4' />
        </button>

        {isImage ? (
          <div className='h-full w-full'>
            <img
              src={URL.createObjectURL(file) || "/placeholder.svg"}
              alt={file?.name}
              className='h-full w-full object-contain'
            />
          </div>
        ) : (
          <div className='flex h-full w-full items-center justify-center'>
            <FileText className='h-10 w-10 text-muted-foreground' />
          </div>
        )}
      </div>

      <div className='mt-1 flex flex-col px-1'>
        <div className='flex items-center gap-1'>
          <input
            type='text'
            className='w-full cursor-text border-b border-muted-foreground px-1 py-0.5 text-sm transition hover:border-primary focus:border-primary focus:outline-none'
            placeholder={`${translate("Click to rename")}`}
            value={nameUpdates[index] || ""}
            onChange={(e) => renameFile(e.target.value)}
          />
        </div>
        <p className='mt-0.5 truncate text-xs text-muted-foreground'>{file.type}</p>
      </div>
    </div>
  )
}
